import {AvailabilityLabels, AvailabilityLabelsVue} from '@/enums/AvailabilityLabels';
import {Weekdays} from '@/helpers/dateHelpers/Weekdays.enum';

/* Pure helpers of the availability week editor (shared by institution, employee, student and partner dialogs) */

/** Weekly availability as edited in the week editor; labels are omitted when the entity has no labels (students) */
export interface EditableAvailability {
    dayOfWeek: number;
    timeRange: { startTime: number; endTime: number };
    labels?: string[];
}

export interface AvailabilityIssue {
    /** Index in the edited array */
    index: number;
    message: string;
}

export const DAY_START = 6 * 60;
export const DAY_END = 20 * 60;
/** Latest minute a time input can represent (23:55) */
export const LAST_MINUTE = 23 * 60 + 55;

export const WORKDAYS = [Weekdays.MONDAY, Weekdays.TUESDAY, Weekdays.WEDNESDAY, Weekdays.THURSDAY, Weekdays.FRIDAY];
export const ALL_DAYS = [...WORKDAYS, Weekdays.SATURDAY, Weekdays.SUNDAY];

/** Labels of every labeled entity (Standort, Lehrkraft, Vertriebspartner-Schule); FOR_MAKEUP is only offered where passed explicitly */
export const ALL_LABELS: string[] = AvailabilityLabelsVue.map((label) => label.value).filter((label) => label !== AvailabilityLabels.FOR_MAKEUP);
/** Labels of Standort and Lehrkraft: additionally "Für Nachholtermine" */
export const LABELS_WITH_MAKEUP: string[] = [...ALL_LABELS, AvailabilityLabels.FOR_MAKEUP];
export const DEFAULT_LABELS: string[] = [AvailabilityLabels.FOR_COURSES];

/** Dot / bar colours of the labels (same meaning as AvailabilityLabelsVue colours) */
const LABEL_COLORS: Record<string, string> = {
    [AvailabilityLabels.FOR_COURSES]: '#1E88E5',
    [AvailabilityLabels.FOR_HANDOUT_EVENTS]: '#43A047',
    [AvailabilityLabels.FOR_ONLINE_COURSES]: '#26A69A',
    [AvailabilityLabels.EXCEPTIONAL]: '#FB8C00',
    [AvailabilityLabels.FOR_MAKEUP]: '#8E24AA',
};

export const PRESETS = [
    {id: 'morning', text: 'Mo–Fr vormittags', hint: '08–13 Uhr', start: 8 * 60, end: 13 * 60},
    {id: 'afternoon', text: 'Mo–Fr nachmittags', hint: '13–17 Uhr', start: 13 * 60, end: 17 * 60},
    {id: 'fullDay', text: 'Mo–Fr ganztags', hint: '08–17 Uhr', start: 8 * 60, end: 17 * 60},
];

export function minutesToTime(minutes: number): string {
    const safe = Math.max(0, Math.min(minutes, 24 * 60));
    const hours = Math.floor(safe / 60);
    const rest = safe % 60;
    return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/** 'HH:mm' to minutes since midnight; undefined for incomplete input */
export function timeToMinutes(time: string): number | undefined {
    const match = /^(\d{1,2}):(\d{2})/.exec(time || '');
    if (!match) {
        return undefined;
    }
    return Number(match[1]) * 60 + Number(match[2]);
}

export function dayShort(day: number): string {
    return (Weekdays.Record[day as Weekdays]?.text || '').slice(0, 2);
}

export function dayText(day: number): string {
    return Weekdays.Record[day as Weekdays]?.text || '';
}

export function labelText(label: string): string {
    return AvailabilityLabelsVue.find((el) => el.value === label)?.text || 'Unbekannt';
}

export function labelColor(label: string): string {
    return LABEL_COLORS[label] || '#90A4AE';
}

/** Deep copy that keeps further stored fields (e.g. _id, createdAt, updatedAt) like the old dialogs did when saving */
export function cloneAvailability<T extends EditableAvailability>(item: T): T {
    const copy: T = {...item, timeRange: {startTime: item.timeRange.startTime, endTime: item.timeRange.endTime}};
    if (item.labels) {
        copy.labels = [...item.labels];
    }
    return copy;
}

/** New entry with only day, range and labels (for copies on other days) */
export function freshAvailability(item: EditableAvailability, dayOfWeek: number): EditableAvailability {
    const copy: EditableAvailability = {dayOfWeek, timeRange: {startTime: item.timeRange.startTime, endTime: item.timeRange.endTime}};
    if (item.labels) {
        copy.labels = [...item.labels];
    }
    return copy;
}

export function sortAvailabilities<T extends EditableAvailability>(items: T[]): T[] {
    return [...items].sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.timeRange.startTime - b.timeRange.startTime);
}

/** Order independent representation to detect changes */
export function availabilitiesKey(items: EditableAvailability[] | null | undefined): string {
    if (!items) {
        return 'null';
    }
    return JSON.stringify(sortAvailabilities(items).map((item) => [
        item.dayOfWeek, item.timeRange.startTime, item.timeRange.endTime, [...(item.labels || [])].sort(),
    ]));
}

/** Two ranges only conflict when they share a label (a course slot may overlap a handout slot); without labels every overlap counts */
function conflicts(a: EditableAvailability, b: EditableAvailability, withLabels: boolean): boolean {
    if (a.dayOfWeek !== b.dayOfWeek) {
        return false;
    }
    const overlaps = a.timeRange.startTime < b.timeRange.endTime && b.timeRange.startTime < a.timeRange.endTime;
    if (!overlaps || !withLabels) {
        return overlaps;
    }
    return (a.labels || []).some((label) => (b.labels || []).includes(label));
}

/** First problem of every invalid entry: end before start, missing label, overlap with another entry of the same day */
export function validateAvailabilities(items: EditableAvailability[], withLabels: boolean): AvailabilityIssue[] {
    const issues: AvailabilityIssue[] = [];
    items.forEach((item, index) => {
        if (item.timeRange.endTime <= item.timeRange.startTime) {
            issues.push({index, message: 'Das Ende muss nach dem Beginn liegen'});
            return;
        }
        if (withLabels && !(item.labels || []).length) {
            issues.push({index, message: 'Mindestens eine Markierung wählen'});
            return;
        }
        const other = items.find((el, i) => i !== index && el.timeRange.endTime > el.timeRange.startTime && conflicts(item, el, withLabels));
        if (other) {
            issues.push({
                index,
                message: `Überschneidet sich mit ${minutesToTime(other.timeRange.startTime)}–${minutesToTime(other.timeRange.endTime)}`,
            });
        }
    });
    return issues;
}

/** "12 Zeitfenster an 5 Tagen" */
export function availabilitySummary(items: EditableAvailability[]): string {
    if (!items.length) {
        return 'Keine Zeitfenster';
    }
    const days = new Set(items.map((item) => item.dayOfWeek)).size;
    return `${items.length} Zeitfenster an ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
}

/** Sensible next range of a day: 08:00–13:00 on an empty day, otherwise up to 4 h after the last range */
export function nextRange(items: EditableAvailability[], day: number): { startTime: number; endTime: number } {
    const ends = items.filter((item) => item.dayOfWeek === day).map((item) => item.timeRange.endTime);
    if (!ends.length) {
        return {startTime: 8 * 60, endTime: 13 * 60};
    }
    const startTime = Math.max(...ends);
    if (startTime >= LAST_MINUTE - 30) {
        return {startTime: 8 * 60, endTime: 13 * 60};
    }
    return {startTime, endTime: Math.min(startTime + 4 * 60, Math.max(startTime + 60, DAY_END), LAST_MINUTE)};
}

/** Adds the preset range on Mo–Fr; days that already have a conflicting range are left untouched */
export function applyPreset(items: EditableAvailability[], start: number, end: number, labels: string[] | undefined): EditableAvailability[] {
    const result = items.map(cloneAvailability);
    const withLabels = !!labels;
    WORKDAYS.forEach((day) => {
        const candidate: EditableAvailability = {dayOfWeek: day, timeRange: {startTime: start, endTime: end}};
        if (labels) {
            candidate.labels = [...labels];
        }
        if (!result.some((item) => conflicts(item, candidate, withLabels))) {
            result.push(candidate);
        }
    });
    return sortAvailabilities(result);
}

/** Replaces the ranges of the target days with copies of the ranges of the source day */
export function copyDay(items: EditableAvailability[], source: number, targets: number[]): EditableAvailability[] {
    const sourceItems = items.filter((item) => item.dayOfWeek === source);
    const kept = items.filter((item) => !targets.includes(item.dayOfWeek) || item.dayOfWeek === source).map(cloneAvailability);
    const copies = targets.filter((day) => day !== source).flatMap((day) => sourceItems.map((item) => freshAvailability(item, day)));
    return sortAvailabilities([...kept, ...copies]);
}
